import React, { useCallback, useEffect, useState } from 'react';
import { Modal } from 'reactstrap';
import { RotateCcw } from 'react-feather';
import { toast } from 'react-toastify';
import {
  TEAM_KEYS,
  TelephonyAgent,
  telephonyApi,
} from '../../../Telephony/telephonyApi';
import { isTelephonyManager } from '../../../Telephony/roles';
import { getStudents, getTeachers } from '../../../Api/apiUtils';
import { useEmailAccessConfig } from '../../../utils/emailAccessConfig';
import { Page, PageHeader, Card, Button, Badge, Field, TextInput, Select, Table, Loader, EmptyState } from '../../../ui';
import TelephonyAdminOnly from './AdminOnly';
import './telephony.scss';

interface TeamOption { key: string; name: string; isActive: boolean; }

/**
 * Telephony → Agents: every CMS user who can call, with their call mode and
 * status. Provisioning here uses the CMS userId as the agent identity — the
 * same id the softphone dock sends — so "add an agent" is the sync mechanism
 * until just-in-time provisioning lands with session federation.
 */
const emptyForm = {
  name: '',
  email: '',
  teamKey: 'sales',
  phoneNumber: '',
};

const TelephonyAgentsPage: React.FC = () => {
  useEmailAccessConfig();
  const [agents, setAgents] = useState<TelephonyAgent[]>([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [teamKeys, setTeamKeys] = useState<string[]>([...TEAM_KEYS]);

  useEffect(() => {
    telephonyApi<TeamOption[]>('/v1/teams')
      .then((teams) =>
        setTeamKeys(teams.filter((t) => t.isActive).map((t) => t.key)),
      )
      .catch(() => undefined); // static fallback already in state
  }, []);
  const [errors, setErrors] = useState<{
    name?: boolean;
    email?: boolean;
    teamKey?: boolean;
    phoneNumber?: boolean;
  }>({});

  const setField = (field: keyof typeof emptyForm, value: string) => {
    setForm((prev) => ({ ...prev, [field]: value }));
    if (value.trim()) setErrors((prev) => ({ ...prev, [field]: false }));
  };

  // The CMS userId is the agent identity but is never typed in: it is
  // resolved from the email at provision time.
  const resolveUserId = async (email: string): Promise<string | null> => {
    const sameEmail = (r: any) =>
      String(r.emailId || r.email || '').trim().toLowerCase() === email.toLowerCase();
    // Staff accounts live in the student records; teacher-staff only in teachers.
    for (const search of [getStudents, getTeachers]) {
      const match = ((await search(0, 5, { emailId: email })) || []).find(sameEmail);
      if (match?.userId) return String(match.userId).trim();
    }
    return null;
  };

  const load = useCallback(() => {
    setLoading(true);
    telephonyApi<TelephonyAgent[]>('/v1/agents')
      .then(setAgents)
      .catch((e) => toast.error(`Agents: ${e.message}`))
      .finally(() => setLoading(false));
  }, []);

  useEffect(load, [load]);

  const createAgent = async () => {
    const nextErrors = {
      name: !form.name.trim(),
      email: !form.email.trim(),
      teamKey: !form.teamKey.trim(),
      // Mandatory: inbound routing falls back to this number when the agent
      // is not online in the browser.
      phoneNumber: !/^\+\d{8,15}$/.test(form.phoneNumber.trim()),
    };
    setErrors(nextErrors);
    if (Object.values(nextErrors).some(Boolean)) return;

    setSaving(true);
    try {
      const phoneNumber = form.phoneNumber.trim();
      const externalId = await resolveUserId(form.email.trim());
      if (!externalId) {
        setErrors((prev) => ({ ...prev, email: true }));
        toast.error('No CMS user found for this email');
        return;
      }
      await telephonyApi('/v1/agents', {
        method: 'POST',
        body: JSON.stringify({
          externalId,
          name: form.name.trim(),
          email: form.email.trim(),
          teamKey: form.teamKey.trim(),
          phoneNumber,
        }),
      });
      // POST /v1/agents ignores phoneNumber when the externalId already
      // exists (its "reactivate an existing agent" path only touches
      // isActive/name/email/team) — so re-provisioning silently drops it.
      // PATCH always applies it, whether this was a fresh create or a
      // reactivate, so it's the reliable way to guarantee it lands.
      await telephonyApi(`/v1/agents/${encodeURIComponent(externalId)}`, {
        method: 'PATCH',
        body: JSON.stringify({ phoneNumber }),
      });
      toast.success(`Agent ${form.name} provisioned`);
      setForm(emptyForm);
      setErrors({});
      load();
    } catch (e) {
      toast.error((e as Error).message);
    } finally {
      setSaving(false);
    }
  };

  // WhatsApp rollout gate — a plain toggle, active immediately. The platform
  // also enforces this server-side on click-to-call.
  const toggleWhatsapp = async (agent: TelephonyAgent) => {
    try {
      await telephonyApi(`/v1/agents/${encodeURIComponent(agent.externalId)}`, {
        method: 'PATCH',
        body: JSON.stringify({ whatsappCallingEnabled: !agent.whatsappCallingEnabled }),
      });
      load();
    } catch (e) {
      toast.error((e as Error).message);
    }
  };

  const toggleActive = async (agent: TelephonyAgent) => {
    try {
      await telephonyApi(`/v1/agents/${encodeURIComponent(agent.externalId)}/active`, {
        method: 'PATCH',
        body: JSON.stringify({ isActive: !agent.isActive }),
      });
      load();
    } catch (e) {
      toast.error((e as Error).message);
    }
  };

  // Edit — PATCH /v1/agents/:externalId. Only name, email, phoneNumber and
  // teamKey are editable; externalId is the identity key (in the URL, not
  // the body) and isn't changeable here.
  const [editingAgent, setEditingAgent] = useState<TelephonyAgent | null>(null);
  const [editForm, setEditForm] = useState({ name: '', email: '', phoneNumber: '', teamKey: '' });
  const [editErrors, setEditErrors] = useState<{ name?: boolean; email?: boolean; phoneNumber?: boolean }>({});
  const [editSaving, setEditSaving] = useState(false);

  const openEdit = (agent: TelephonyAgent) => {
    setEditingAgent(agent);
    setEditForm({
      name: agent.name,
      email: agent.email,
      phoneNumber: agent.phoneNumber ?? '',
      teamKey: agent.team?.key ?? teamKeys[0] ?? '',
    });
    setEditErrors({});
  };

  const setEditField = (field: keyof typeof editForm, value: string) => {
    setEditForm((prev) => ({ ...prev, [field]: value }));
    if (field !== 'phoneNumber' && value.trim()) setEditErrors((prev) => ({ ...prev, [field]: false }));
    // Phone is optional on edit (empty clears it) — only clear its error once it's valid or empty.
    if (field === 'phoneNumber' && (!value.trim() || /^\+\d{8,15}$/.test(value.trim()))) {
      setEditErrors((prev) => ({ ...prev, phoneNumber: false }));
    }
  };

  const saveEdit = async () => {
    if (!editingAgent) return;
    const phoneNumber = editForm.phoneNumber.trim();
    const nextErrors = {
      name: !editForm.name.trim(),
      email: !editForm.email.trim(),
      phoneNumber: phoneNumber !== '' && !/^\+\d{8,15}$/.test(phoneNumber),
    };
    setEditErrors(nextErrors);
    if (Object.values(nextErrors).some(Boolean)) return;

    setEditSaving(true);
    try {
      await telephonyApi(`/v1/agents/${encodeURIComponent(editingAgent.externalId)}`, {
        method: 'PATCH',
        body: JSON.stringify({
          name: editForm.name.trim(),
          email: editForm.email.trim(),
          phoneNumber,
          teamKey: editForm.teamKey,
        }),
      });
      toast.success(`Agent ${editForm.name} updated`);
      setEditingAgent(null);
      load();
    } catch (e) {
      toast.error((e as Error).message);
    } finally {
      setEditSaving(false);
    }
  };

  if (!isTelephonyManager()) {
    return <TelephonyAdminOnly title="Agents" />;
  }

  const teamOptions = teamKeys.map((t) => ({ value: t, label: t }));
  const PHONE_ERROR = 'Use +countrycode, e.g. +9198…';

  return (
    <Page>
      <PageHeader
        eyebrow="Telephony"
        title="Agents"
        subtitle={loading ? 'Loading agents…' : `${agents.length} agents · ${agents.filter((a) => a.isActive).length} active`}
        actions={
          <Button variant="secondary" icon={<RotateCcw size={16} aria-hidden />} onClick={load} disabled={loading}>
            Refresh
          </Button>
        }
      />

      <Card title="Provision agent">
        {/* Top-aligned: validation text below a field must not shift its neighbours. */}
        <div className="tel-form-row">
          <Field label="Name" required error={errors.name ? 'Required' : undefined}>
            {(ctl) => (
              <TextInput {...ctl} value={form.name} onChange={(e) => setField('name', e.target.value)} />
            )}
          </Field>
          <Field label="Email" required error={errors.email ? 'Required' : undefined}>
            {(ctl) => (
              <TextInput
                {...ctl}
                value={form.email}
                onChange={(e) => setField('email', e.target.value)}
              />
            )}
          </Field>
          <Field label="Team" required error={errors.teamKey ? 'Required' : undefined}>
            {(ctl) => (
              <Select {...ctl} options={teamOptions} value={form.teamKey} onChange={(e) => setField('teamKey', e.target.value)} />
            )}
          </Field>
          <Field
            label="Phone"
            required
            hint="Callbacks ring this number when the agent is not online in the browser"
            error={errors.phoneNumber ? PHONE_ERROR : undefined}
          >
            {(ctl) => (
              <TextInput
                {...ctl}
                value={form.phoneNumber}
                placeholder="+919876543210"
                onChange={(e) => setField('phoneNumber', e.target.value)}
              />
            )}
          </Field>
          <div className="tel-form-row__submit">
            <Button variant="primary" loading={saving} onClick={createAgent}>
              {saving ? 'Saving…' : 'Provision'}
            </Button>
          </div>
        </div>
      </Card>

      <Card flush title="Telephony agents">
        {loading ? (
          <Loader label="Loading agents…" />
        ) : agents.length === 0 ? (
          <div className="tel-flush-pad">
            <EmptyState title="No agents provisioned yet" description="Provision an agent above with their email." />
          </div>
        ) : (
          <Table caption="Telephony agents">
            <thead>
              <tr>
                <th>Name</th><th>Email</th><th>Team</th>
                <th>Phone</th><th>WhatsApp</th><th>Status</th><th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {agents.map((a) => (
                <tr key={a.id} className={a.isActive ? undefined : 'tel-inactive'}>
                  <td>{a.name}</td>
                  <td>{a.email}</td>
                  <td>{a.team?.name ?? '—'}</td>
                  <td className="text-nowrap">{a.phoneNumber ?? '—'}</td>
                  <td>
                    <div className="form-check form-switch tel-switch">
                      <input
                        className="form-check-input"
                        type="checkbox"
                        role="switch"
                        aria-label={`WhatsApp calling for ${a.name}`}
                        title={a.whatsappCallingEnabled ? 'WhatsApp calling enabled — click to disable' : 'Enable WhatsApp calling for this agent'}
                        checked={!!a.whatsappCallingEnabled}
                        onChange={() => toggleWhatsapp(a)}
                      />
                    </div>
                  </td>
                  <td>
                    <Badge tone={a.isActive ? 'success' : 'danger'}>
                      {a.isActive ? 'active' : 'inactive'}
                    </Badge>
                  </td>
                  <td>
                    <div className="tel-row-actions">
                      <Button size="sm" variant="secondary" onClick={() => openEdit(a)}>
                        Edit
                      </Button>
                      <Button size="sm" variant="secondary" onClick={() => toggleActive(a)}>
                        {a.isActive ? 'Deactivate' : 'Activate'}
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        )}
      </Card>

      <Modal isOpen={!!editingAgent} toggle={() => setEditingAgent(null)} centered className="ds-dialog" returnFocusAfterClose>
        <h2 className="ds-dialog__title">
          Edit agent
        </h2>
        <div className="ds-stack">
          <Field label="Name" required error={editErrors.name ? 'Required' : undefined}>
            {(ctl) => (
              <TextInput {...ctl} value={editForm.name} onChange={(e) => setEditField('name', e.target.value)} />
            )}
          </Field>
          <Field label="Email" required error={editErrors.email ? 'Required' : undefined}>
            {(ctl) => (
              <TextInput {...ctl} value={editForm.email} onChange={(e) => setEditField('email', e.target.value)} />
            )}
          </Field>
          <Field label="Phone" error={editErrors.phoneNumber ? PHONE_ERROR : undefined}>
            {(ctl) => (
              <TextInput
                {...ctl}
                value={editForm.phoneNumber}
                placeholder="+919876543210"
                onChange={(e) => setEditField('phoneNumber', e.target.value)}
              />
            )}
          </Field>
          <Field label="Team" required>
            {(ctl) => (
              <Select {...ctl} options={teamOptions} value={editForm.teamKey} onChange={(e) => setEditField('teamKey', e.target.value)} />
            )}
          </Field>
        </div>
        <div className="ds-dialog__actions">
          <Button variant="secondary" onClick={() => setEditingAgent(null)}>Cancel</Button>
          <Button variant="primary" loading={editSaving} onClick={saveEdit}>
            {editSaving ? 'Saving…' : 'Save'}
          </Button>
        </div>
      </Modal>
    </Page>
  );
};

export default TelephonyAgentsPage;
